import { globalStore } from './globalStore';
import { UserRoleManager } from './userRole';
import { useEffect, useState } from 'react';
import type { UserRoleInfo } from '../types/index';

/**
 * 视频Tag按钮权限管理工具类
 */
export class VideoTagPermissionManager {
  /**
   * 检查视频Tag按钮是否应该显示
   * @param videoStatus 视频状态
   * @param videoTag 视频tag值
   * @returns 是否显示Tag按钮
   */
  static shouldShowTagButton(videoStatus: number, videoTag: number): boolean {
    // 视频状态必须是2（完成）
    if (videoStatus !== 2) {
      return false;
    }

    // 检查是否具有标记按钮权限
    if (!this.hasMenuPermission()) {
      return false;
    }

    // 显式获取最新用户信息，优先从 globalStore 获取，确保角色信息是最新的
    const userInfo = globalStore.getUserInfo();
    const userRole = userInfo 
      ? UserRoleManager.getUserRole(userInfo) 
      : UserRoleManager.getCurrentUserRole();
    
    if (!userRole) {
      return false;
    }

    // 根据角色判断显示条件
    // 说明：
    // - 操作者（operator）：tag 不等于 1 才显示
    // - 审核员（reviewer）：tag 不等于 0 且不等于 2（需返工）才显示
    // - 管理员 / 超级管理员（admin / super_admin）：与审核员相同逻辑，tag 不等于 0 且不等于 2（需返工）才显示

    if ((userRole as any).operator || userRole.isOperator) {
      return videoTag !== 1;
    } else if (
      (userRole as any).reviewer ||
      userRole.isAssessor ||
      userRole.isAdmin
    ) {
      // 如果视频是需返工（tag = 2），管理员、审核者和超级管理员不显示打标记按钮
      return videoTag !== 0 && videoTag !== 2;
    }
    return false;
  }

  /**
   * 获取Tag按钮点击后的操作选项
   * @param videoTag 视频tag值
   * @returns 操作选项数组
   */
  static getTagButtonActions(videoTag: number): Array<{key: string, text: string}> {
    // 显式获取最新用户信息，优先从 globalStore 获取，确保角色信息是最新的
    const userInfo = globalStore.getUserInfo();
    const userRole = userInfo 
      ? UserRoleManager.getUserRole(userInfo) 
      : UserRoleManager.getCurrentUserRole();
    
    if (!userRole) {
      return [];
    }

    if (userRole.isOperator) {
      // 操作者的操作选项
      if (videoTag === 0 || videoTag === 2) {
        return [{ key: 'submit', text: '提交' }];
      } else if (videoTag === 3 || videoTag === 4) {
        return [{ key: 'revoke', text: '撤销' }];
      }
    } else if (userRole.isAssessor || userRole.isAdmin) {
      // 审核员或管理员的操作选项
      // 需求：
      // - 当 tag 状态为 3 时，同时显示【返工】和【审核通过】
      // - 当 tag 状态为 1 时，只显示【返工】
      if (videoTag === 1) {
        return [
          { key: 'rework', text: '返工' }
        ];
      }
      return [
        { key: 'rework', text: '返工' },
        { key: 'approve', text: '审核通过' }
      ];
    }

    return [];
  }

  /**
   * 检查是否有标记按钮权限
   * 权限标识：video:multilingual:mark
   * 逻辑与 Home 页创建剧集按钮的权限检查方式一致：
   * 1. 先查用户 permissions 数组
   * 2. 再查菜单 meta.perms
   */
  private static hasMenuPermission(): boolean {
    const targetPermission = 'video:multilingual:mark';

    // 1. 先看用户权限数组
    const userInfo = globalStore.getUserInfo();
    const userPermissions: string[] = (userInfo && (userInfo as any).permissions) || [];
    if (
      userPermissions &&
      userPermissions.length > 0 &&
      userPermissions.includes(targetPermission)
    ) {
      return true;
    }

    // 2. 再看菜单中的 meta.perms
    const menuList = globalStore.getMenuList() || [];
    if (!menuList || menuList.length === 0) {
      return false;
    }

    const findMenuPermission = (menus: any[], perm: string): boolean => {
      for (const menu of menus) {
        if (menu.meta?.perms === perm) {
          return true;
        }
        if (menu.children && menu.children.length > 0) {
          if (findMenuPermission(menu.children, perm)) {
            return true;
          }
        }
      }
      return false;
    };

    const hasPerm = findMenuPermission(menuList, targetPermission);
    if (!hasPerm) {
    }
    return hasPerm;
  }
}

/**
 * React Hook for video tag button permissions
 */
export const useVideoTagPermission = () => {
  // 实时获取最新的用户角色信息
  const [userRole, setUserRole] = useState<UserRoleInfo | null>(() => {
    // 初始化时从 globalStore 获取
    const globalUserInfo = globalStore.getUserInfo();
    if (globalUserInfo) {
      return UserRoleManager.getUserRole(globalUserInfo);
    }
    return UserRoleManager.getCurrentUserRole();
  });

  // 监听 globalStore 变化，实时更新角色信息
  useEffect(() => {
    const updateRole = () => {
      const globalUserInfo = globalStore.getUserInfo();
      if (globalUserInfo) {
        const role = UserRoleManager.getUserRole(globalUserInfo);
        setUserRole(role);
      } else {
        const role = UserRoleManager.getCurrentUserRole();
        setUserRole(role);
      }
    };

    // 立即更新一次
    updateRole();

    // 延迟更新多次，确保在页面跳转后 globalStore 已完全初始化
    const timeoutId1 = setTimeout(() => {
      updateRole();
    }, 50);
    
    const timeoutId2 = setTimeout(() => {
      updateRole();
    }, 200);

    // 订阅 globalStore 变化
    const unsubscribe = globalStore.subscribe(updateRole);
    
    return () => {
      clearTimeout(timeoutId1);
      clearTimeout(timeoutId2);
      unsubscribe();
    };
  }, []);

  /**
   * 检查是否应该显示Tag按钮
   */
  const shouldShowTagButton = (videoStatus: number, videoTag: number): boolean => {
    return VideoTagPermissionManager.shouldShowTagButton(videoStatus, videoTag);
  };

  /**
   * 获取Tag按钮的操作选项
   * 使用实时更新的角色信息，确保角色判断准确
   * 每次调用时都重新获取最新的角色信息，避免页面跳转时角色信息未更新
   */
  const getTagButtonActions = (videoTag: number): Array<{key: string, text: string}> => {
    // 每次调用时都重新获取最新的角色信息，确保角色判断准确
    // 优先从 globalStore 获取，如果为空则从 localStorage 获取
    let currentRole: UserRoleInfo | null = null;
    
    const globalUserInfo = globalStore.getUserInfo();
    if (globalUserInfo) {
      currentRole = UserRoleManager.getUserRole(globalUserInfo);
      // 调试日志：输出角色判断信息
      console.log('[getTagButtonActions] globalUserInfo:', {
        roles: (globalUserInfo as any)?.roles,
        rolesDetail: (globalUserInfo as any)?.roles?.map((r: any) => ({
          raw: r,
          key: typeof r === 'string' ? r : r?.role_key || r?.code || r?.key || ''
        })),
        role: (globalUserInfo as any)?.role,
        currentRole: currentRole ? {
          role: currentRole.role,
          isAdmin: currentRole.isAdmin,
          isAssessor: currentRole.isAssessor,
          isOperator: currentRole.isOperator
        } : null
      });
    }
    
    // 如果 globalStore 中没有，尝试从 localStorage 获取
    if (!currentRole) {
      currentRole = UserRoleManager.getCurrentUserRole();
      console.log('[getTagButtonActions] 从 localStorage 获取角色:', currentRole ? {
        role: currentRole.role,
        isAdmin: currentRole.isAdmin,
        isAssessor: currentRole.isAssessor,
        isOperator: currentRole.isOperator
      } : null);
    }
    
    // 如果还是没有，尝试从 hook 内部状态获取（作为最后的回退）
    if (!currentRole && userRole) {
      currentRole = userRole;
      console.log('[getTagButtonActions] 从 hook 内部状态获取角色:', {
        role: currentRole.role,
        isAdmin: currentRole.isAdmin,
        isAssessor: currentRole.isAssessor,
        isOperator: currentRole.isOperator
      });
    }
    
    if (!currentRole) {
      console.warn('[getTagButtonActions] 无法获取角色信息');
      return [];
    }

    console.log('[getTagButtonActions] 最终使用的角色:', {
      role: currentRole.role,
      isAdmin: currentRole.isAdmin,
      isAssessor: currentRole.isAssessor,
      isOperator: currentRole.isOperator,
      videoTag
    });

    if (currentRole.isOperator) {
      // 操作者的操作选项
      if (videoTag === 0 || videoTag === 2) {
        return [{ key: 'submit', text: '提交' }];
      } else if (videoTag === 3 || videoTag === 4) {
        return [{ key: 'revoke', text: '撤销' }];
      }
    } else if (currentRole.isAssessor || currentRole.isAdmin) {
      // 审核员或管理员的操作选项
      if (videoTag === 1) {
        return [
          { key: 'rework', text: '返工' }
        ];
      }
      return [
        { key: 'rework', text: '返工' },
        { key: 'approve', text: '审核通过' }
      ];
    }

    return [];
  };

  return {
    shouldShowTagButton,
    getTagButtonActions
  };
};
